import { useState } from 'react';
import { User, Mail, Lock, Check, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  updateProfile, changePassword, requestEmailChange, cancelEmailChange,
} from '../api';

type Mode = 'view' | 'edit-name' | 'change-email' | 'change-password';

export default function ProfileSection() {
  const { user, refresh } = useAuth();
  const [mode, setMode]     = useState<Mode>('view');
  const [error, setError]   = useState('');
  const [info, setInfo]     = useState('');
  const [working, setWorking] = useState(false);

  // Form state per mode
  const [name, setName]       = useState(user?.name ?? '');
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]     = useState('');
  const [newPw2, setNewPw2]   = useState('');

  if (!user) return null;

  const reset = () => {
    setName(user.name); setNewEmail(''); setEmailPw('');
    setCurrentPw(''); setNewPw(''); setNewPw2('');
    setError(''); setInfo('');
  };

  const saveName = async () => {
    setError(''); setInfo(''); setWorking(true);
    try {
      await updateProfile(name.trim());
      await refresh();
      setInfo('Navn opdateret');
      setMode('view');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  const saveEmailChange = async () => {
    setError(''); setInfo(''); setWorking(true);
    try {
      const r = await requestEmailChange(newEmail.trim(), emailPw);
      setInfo(`Verifikations-link sendt til ${r.pending_email}. Email opdateres når du klikker linket.`);
      reset();
      await refresh();
      setMode('view');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  const cancelPending = async () => {
    setError(''); setWorking(true);
    try {
      await cancelEmailChange();
      await refresh();
      setInfo('Anmodning annulleret');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  const savePassword = async () => {
    setError(''); setInfo(''); setWorking(true);
    try {
      if (newPw !== newPw2) { setError('De to passwords matcher ikke'); setWorking(false); return; }
      if (newPw.length < 8) { setError('Password skal være mindst 8 tegn'); setWorking(false); return; }
      await changePassword(currentPw, newPw);
      reset();
      setInfo('Password ændret');
      setMode('view');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  // ── VIEW ─────────────────────────────────────────────────────────────────
  if (mode === 'view') {
    return (
      <div className="space-y-3">
        {/* Navn */}
        <Row icon={<User size={18} />} label="Navn" value={user.name}
          onEdit={() => { setName(user.name); setMode('edit-name'); }} />

        {/* Email */}
        <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-lg flex items-center justify-center bg-sage-100 text-sage-700">
                <Mail size={18} />
              </span>
              <div>
                <h3 className="text-base font-semibold text-ink-900">Email</h3>
                <p className="text-sm text-ink-500 mt-0.5 font-mono">{user.email}</p>
                {user.pendingEmail && (
                  <p className="text-xs text-sage-700 mt-2 inline-flex items-center gap-1.5 bg-sage-100 px-2 py-1 rounded">
                    <AlertTriangle size={12} /> Venter på verifikation: <span className="font-mono">{user.pendingEmail}</span>
                  </p>
                )}
              </div>
            </div>
            {user.pendingEmail ? (
              <button onClick={cancelPending} disabled={working} className="btn btn-secondary text-sm py-1.5 px-3">
                Annullér
              </button>
            ) : (
              <button onClick={() => { setNewEmail(''); setEmailPw(''); setMode('change-email'); }} className="btn btn-secondary text-sm py-1.5 px-3">
                Skift
              </button>
            )}
          </div>
        </div>

        {/* Password */}
        <Row icon={<Lock size={18} />} label="Adgangskode" value="••••••••••"
          onEdit={() => { setCurrentPw(''); setNewPw(''); setNewPw2(''); setMode('change-password'); }} />

        {info && <p className="text-sm text-sage-700 inline-flex items-center gap-1.5"><Check size={14} />{info}</p>}
        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
    );
  }

  // ── EDIT NAME ────────────────────────────────────────────────────────────
  if (mode === 'edit-name') {
    return (
      <FormCard icon={<User size={18} />} title="Skift navn">
        <label className="label">Navn</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          className="input" autoFocus />
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={saveName} disabled={working || !name.trim()} className="btn btn-primary">
            {working ? 'Gemmer…' : 'Gem'}
          </button>
          <button onClick={() => { reset(); setMode('view'); }} className="btn btn-secondary">Annuller</button>
        </div>
      </FormCard>
    );
  }

  // ── CHANGE EMAIL ─────────────────────────────────────────────────────────
  if (mode === 'change-email') {
    return (
      <FormCard icon={<Mail size={18} />} title="Skift email-adresse">
        <p className="text-sm text-ink-500 mb-4">
          Vi sender et bekræftelses-link til den nye email. Din email opdateres først når du klikker linket.
          Du skal også indtaste dit nuværende password for at bekræfte at det er dig.
        </p>
        <div className="space-y-3">
          <div>
            <label className="label">Ny email-adresse</label>
            {/* autoComplete="off" + unik name forhindrer Chrome i at autofylde det gamle login-email */}
            <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
              className="input" autoFocus
              name="rfo-new-email" autoComplete="off" />
          </div>
          <div>
            <label className="label">Nuværende password</label>
            <input type="password" value={emailPw} onChange={e => setEmailPw(e.target.value)}
              className="input" autoComplete="current-password" />
          </div>
        </div>
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={saveEmailChange} disabled={working || !newEmail.trim() || !emailPw} className="btn btn-primary">
            {working ? 'Sender…' : 'Send bekræftelses-link'}
          </button>
          <button onClick={() => { reset(); setMode('view'); }} className="btn btn-secondary">Annuller</button>
        </div>
      </FormCard>
    );
  }

  // ── CHANGE PASSWORD ──────────────────────────────────────────────────────
  if (mode === 'change-password') {
    return (
      <FormCard icon={<Lock size={18} />} title="Skift password">
        <div className="space-y-3">
          <div>
            <label className="label">Nuværende password</label>
            <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
              className="input" autoComplete="current-password" autoFocus />
          </div>
          <div>
            <label className="label">Nyt password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
              className="input" autoComplete="new-password" placeholder="Min 8 tegn" />
          </div>
          <div>
            <label className="label">Bekræft nyt password</label>
            <input type="password" value={newPw2} onChange={e => setNewPw2(e.target.value)}
              className="input" autoComplete="new-password" />
          </div>
        </div>
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={savePassword} disabled={working || !currentPw || !newPw || !newPw2} className="btn btn-primary">
            {working ? 'Gemmer…' : 'Skift password'}
          </button>
          <button onClick={() => { reset(); setMode('view'); }} className="btn btn-secondary">Annuller</button>
        </div>
      </FormCard>
    );
  }

  return null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Row({ icon, label, value, onEdit }: {
  icon: React.ReactNode; label: string; value: string; onEdit: () => void;
}) {
  return (
    <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-lg flex items-center justify-center bg-sage-100 text-sage-700">
            {icon}
          </span>
          <div>
            <h3 className="text-base font-semibold text-ink-900">{label}</h3>
            <p className="text-sm text-ink-500 mt-0.5">{value}</p>
          </div>
        </div>
        <button onClick={onEdit} className="btn btn-secondary text-sm py-1.5 px-3">Skift</button>
      </div>
    </div>
  );
}

function FormCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-lg bg-sage-100 text-sage-700 flex items-center justify-center">{icon}</span>
        <h3 className="modal-title">{title}</h3>
      </div>
      {children}
    </div>
  );
}
